// vertex shader
const vertexShader = `
uniform float iTime;

varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

// fragment shader
const fragmentShader = `
uniform sampler2D mapA;
uniform sampler2D mapB;
uniform float iTime;
uniform float progress;
varying vec2 vUv;
uniform int transitionMode;
uniform vec2 direction;

#define TEMPORAL 1
#define GOLDEN_X 25
#define GOLDEN_Y 65535
float mod1(float x) {
  return x - floor(x);
}

vec4 pixTex(sampler2D tex, vec2 uv) {
  vec2 res = vec2(textureSize(tex, 0));
  uv = uv * res;
  vec2 seam = floor(uv + 0.5);
  uv = seam + clamp((uv - seam) / fwidth(uv), -0.5, 0.5);
  return texture(tex, uv / res);
}

float PHI = 1.61803398874989484820459 * 00000.1; // Golden Ratio   
float PI  = 3.14159265358979323846264 * 00000.1; // PI
float SQ2 = 1.41421356237309504880169 * 10000.0; // Square Root of Two
float E   = 2.71828182846;

////////////////////////////////////////////////////////////////////////////////
// https://www.shadertoy.com/view/ltB3zD
float gold_noise(in vec2 coordinate, in float seed) {
  return fract(tan(distance(coordinate * (seed + PHI), vec2(PHI, PI))) * SQ2);
}
////////////////////////////////////////////////////////////////////////////////

float gold_noise3(in vec3 coordinate, in float seed) {
  return fract(tan(distance(coordinate * (seed + PHI), vec3(PHI, PI, E))) * SQ2);
}

vec3 mod289(vec3 x) {
  return x - floor(x * (1.0 / 289.0)) * 289.0;
}

vec2 mod289(vec2 x) {
  return x - floor(x * (1.0 / 289.0)) * 289.0;
}

vec3 permute(vec3 x) {
  return mod289(((x * 34.0) + 10.0) * x);
}

float random(in vec2 _st) {
  return fract(sin(dot(_st.xy, vec2(12.9898, 78.233))) * 43758.5453123);
}

// simple 2D Value noise function
// based on Morgan McGuire @morgan3d
// https://www.shadertoy.com/view/4dS3Wd
float noise(in vec2 _st) {
  vec2 i = floor(_st);
  vec2 f = fract(_st);

  // four corners in 2D of a tile
  float a = random(i);
  float b = random(i + vec2(1.0, 0.0));
  float c = random(i + vec2(0.0, 1.0));
  float d = random(i + vec2(1.0, 1.0));

  vec2 u = f * f * (3.0 - 2.0 * f);

  return mix(a, b, u.x) +
          (c - a) * u.y * (1.0 - u.x) +
          (d - b) * u.x * u.y;
}

#define NUM_OCTAVES 6

float fbm(in vec2 _st) {
  float v = 0.0;
  float a = 0.5; // fbmInitialAmplitude
  vec2 shift = vec2(200.0, 200.0); // fbmShift
  float fbmRotationAngle = 0.5; // radians
  float fbmFrequencyMult = 2.0;
  float fbmAmplitudeDecay = 0.5;
  // rotate to reduce axial bias
  mat2 rot = mat2(
    cos(fbmRotationAngle), sin(fbmRotationAngle),
    -sin(fbmRotationAngle), cos(fbmRotationAngle)
  );
  for (int i = 0; i < NUM_OCTAVES; ++i) {
    v += a * noise(_st);
    _st = rot * _st * fbmFrequencyMult + shift;
    a *= fbmAmplitudeDecay;
  }
  return v;
}

float snoise(vec2 v) {
  const vec4 C = vec4(
    0.211324865405187,  // (3.0-sqrt(3.0))/6.0
    0.366025403784439,  // 0.5*(sqrt(3.0)-1.0)
    -0.577350269189626, // -1.0 + 2.0 * C.x
    0.024390243902439   // 1.0 / 41.0
  );
  // first corner
  vec2 i = floor(v + dot(v, C.yy));
  vec2 x0 = v - i + dot(i, C.xx);

  // other corners
  vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
  vec4 x12 = x0.xyxy + C.xxzz;
  x12.xy -= i1;

  // permutations
  i = mod289(i); // Avoid truncation effects in permutation
  vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0)) + i.x + vec3(0.0, i1.x, 1.0));

  vec3 m = max(0.5 - vec3(dot(x0, x0), dot(x12.xy, x12.xy), dot(x12.zw, x12.zw)), 0.0);
  m = m * m;
  m = m * m;

  // gradients: 41 points uniformly over a line, mapped onto a diamond.
  // the ring size 17*17 = 289 is close to a multiple of 41 (41*7 = 287)

  vec3 x = 2.0 * fract(p * C.www) - 1.0;
  vec3 h = abs(x) - 0.5;
  vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;

  // normalise gradients implicitly by scaling m
  // approximation of: m *= inversesqrt( a0*a0 + h*h );
  m *= 1.79284291400159 - 0.85373472095314 * (a0 * a0 + h * h);

  // compute final noise value at P
  vec3 g;
  g.x = a0.x * x0.x + h.x * x0.y;
  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  return 130.0 * dot(m, g);
}

// hSL to RGB conversion
vec3 hsl2rgb(vec3 hsl) {
  float C = (1.0 - abs(2.0 * hsl.z - 1.0)) * hsl.y;
  float X = C * (1.0 - abs(mod(hsl.x * 6.0, 2.0) - 1.0));
  float m = hsl.z - 0.5 * C;
  vec3 rgb = hsl.x < 1.0 / 6.0 ? vec3(C, X, 0.0) :
              hsl.x < 2.0 / 6.0 ? vec3(X, C, 0.0) :
              hsl.x < 3.0 / 6.0 ? vec3(0.0, C, X) :
              hsl.x < 4.0 / 6.0 ? vec3(0.0, X, C) :
              hsl.x < 5.0 / 6.0 ? vec3(X, 0.0, C) : vec3(C, 0.0, X);
  return rgb + m;
}

float softMax(float a, float b, float k) {
  return log(exp(k * a) + exp(k * b)) / k;
}

float softMin(float a, float b, float k) {
  return -softMax(-a, -b, k);
}

float circleSDF(vec2 pos, float rad) {
  float circle = length(pos);
  circle = step(circle, rad);
  return circle;
}
  
#define PI 3.14159265359
#define rot(a) mat2(cos(a + PI * vec4(0, 1.5, 0.5, 0)))

// simple 9-tap box blur
vec4 blur(sampler2D tex, vec2 uv, float radius) {
  vec4 sum = vec4(0.0);
  float total = 0.0;
  for (int x = -1; x <= 1; x++) {
    for (int y = -1; y <= 1; y++) {
      float weight = 1.0 - 0.5 * (abs(float(x)) + abs(float(y)));
      sum += texture(tex, uv + vec2(x, y) * radius) * weight;
      total += weight;
    }
  }
  return sum / total;
}

vec4 directionalBlur(sampler2D tex, vec2 uv, float radius, vec2 direction) {
  vec4 sum = vec4(0.0);
  float total = 0.0;
  // sample along the direction vector
  for (int i = -4; i <= 4; i++) {
    float t = float(i) / 4.0; // normalize to [-1, 1]
    float weight = 1.0 - abs(t); // simple linear weight, can use Gaussian for smoother
    sum += texture(tex, uv + direction * t * radius) * weight;
    total += weight;
  }
  return sum / total;
}

vec4 FBM(sampler2D texA, sampler2D texB, vec2 uv, float time) {
  vec2 p = uv;
  float n = fbm(p * 0.5 + time);
  float prog = smoothstep(0.0, 1.0, n);
  vec4 colorA = texture(texA, uv);
  vec4 colorB = texture(texB, uv);
  return mix(colorA, colorB, prog);
}

vec4 noisyFBM(
  sampler2D sceneA,
  sampler2D sceneB,
  vec2 uv,
  float time,
  float scale,
  float speed,
  float progress,
  float amplitude
) {
  
  float n = fbm(uv * scale + time * speed * progress);
  float noisyProgress = clamp(progress + (n - 2.0) * amplitude * 2.0, 0.0, 1.0);
  float mask = smoothstep(0.0, 1.0, noisyProgress);
  vec4 colorA = texture(sceneA, uv);
  vec4 colorB = texture(sceneB, uv);
  return mix(colorA, colorB, mask);
}

vec4 domainWarped(sampler2D texA, sampler2D texB, vec2 uv, float time, float progress) {
  vec2 warpedUV = uv + 0.2 * vec2(fbm(uv * 2.0 + time), fbm(uv * 2.0 - time));
  float n = fbm(warpedUV);
  float mask = smoothstep(0.0, 1.0, progress + (n - 0.5) * 0.5);
  vec4 colorA = texture(texA, uv);
  vec4 colorB = texture(texB, uv);
  return mix(colorA, colorB, mask);
}

vec4 noisyDomainWarped(sampler2D texA, sampler2D texB, vec2 uv, float time, float progress) {
  vec2 warpedUV = uv + 0.2 * vec2(fbm(uv * 2.0 + time), fbm(uv * 2.0 - time));
  float n = fbm(warpedUV);
  float noisyProgress = clamp(progress + (n - 0.5) * 0.5, 0.0, 1.0);
  float mask = smoothstep(0.5, 0.5, noisyProgress);
  vec4 colorA = texture(texA, uv);
  vec4 colorB = texture(texB, uv);
  return mix(colorA, colorB, mask);
}

void main() {
  float scale = 1.0; 
  float prog = progress * 1.0;
  float angle = PI * prog; // rotation angle in radians
  
  mat2 rot = mat2(cos(angle), -sin(angle), sin(angle), cos(angle));
  vec2 warpedUV = rot * vUv * direction * scale;
  vec2 warpedUVNoRotation =  vUv * direction * scale;
  
  vec4 texelA = texture(mapA, warpedUV);
  vec4 texelB = texture(mapB, warpedUV);

  float seed = fract(sin(iTime * 0.1));
  vec4 col_rgba = vec4 (gold_noise(warpedUV, seed+0.1),  // r
                      gold_noise(warpedUV, seed+0.2),  // g
                      gold_noise(warpedUV, seed+0.3),  // b
                      gold_noise(warpedUV, seed+0.4)); // α
                      
  // modulate progress with noise for a noisy transition
  // float gn3 = gold_noise3(col_rgba.rgb, seed);
  float smootherSeed = sin(iTime * 0.15); // Much slower variation
  float gn3 = gold_noise3(col_rgba.rgb, smootherSeed);
  float n = fbm(warpedUV * prog * seed);
  float gnws = dot(col_rgba.rgb, vec3(0.299, 0.587, 0.114)); // weighted sum - luminance
  float noisyProg1 = clamp(prog + (n - 0.5) * seed, 0.0, 1.0);
  // float noisyProg2 = clamp(prog + (gn3 - 0.5) * seed, 0.0, 1.0);
  float noiseAmount = 0.3; // Adjust from 0.0 (no noise) to 1.0 (full noise)
  float noisyProg2 = mix(prog, clamp(prog + (gn3 - 0.5) * seed, 0.0, 1.0), noiseAmount);
  float noisyProg4 = clamp(prog + (gn3 - 0.5) * seed * 0.3, 0.0, 1.0);
  float noisyProg3 = clamp(prog + (gnws - 0.5) * seed, 0.0, 1.0);

  // wipe
  // wipe between texelA and texelB
  float cut = smoothstep(0.0, 0.0, warpedUVNoRotation.x - warpedUVNoRotation.y - 5.0 + 5.0 * prog * 1.2);
  float noisyCut = smoothstep(0.0, 0.0, warpedUV.x - warpedUV.y - 5.0 + 5.0 * gn3);
  vec4 colorWipe = mix(texelA, texelB, cut);
  vec4 colorNoisyWipe = mix(texelA, texelB, noisyCut);

  // blur
  // blur amount for each scene
  // float blurA = 1.0 - prog; // mapA sharpens as progress increases
  // float blurB = prog;       // mapB blurs as progress increases

  //   // With either cubic easing:
  // float cubicProgress = prog < 0.5
  //   ? 4.0 * prog * prog * prog
  //   : 1.0 - pow(-2.0 * prog + 2.0, 3.0) / 2.0;
  // float blurA = 1.0 - cubicProgress;
  // float blurB = cubicProgress;

  // or exponential easing (for even more gradual transitions):
  float expProgress = prog == 0.0
    ? 0.0
    : prog == 1.0
      ? 1.0
      : prog < 0.5
        ? pow(2.0, 20.0 * prog - 10.0) / 2.0
        : (2.0 - pow(2.0, -20.0 * prog + 10.0)) / 2.0;
  float blurA = 1.0 - expProgress;
  float blurB = expProgress;



  float maxRad = 4.0; // max blur radius
  vec4 blurredA = blur(mapA, warpedUV, blurA * maxRad);
  vec4 blurredB = blur(mapB, warpedUV, blurB * maxRad);
  vec4 dirBlurredA = directionalBlur(mapA, warpedUV, blurA * maxRad, direction);
  vec4 dirBlurredB = directionalBlur(mapB, warpedUV, blurB * maxRad, direction);
  vec4 colorBlur = mix(blurredA, blurredB, prog);
  vec4 colorNoisyBlur = mix(
    mix(blurredA, blurredB, max(0.0, prog - 0.05)),
    mix(blurredA, blurredB, min(1.0, prog + 0.05)),
    (noisyProg2 * 0.5) + 0.25
  );
  colorNoisyBlur = mix(blurredA, blurredB, noisyProg2);

  vec4 colorDirBlur = mix(dirBlurredA, dirBlurredB, prog);
  vec4 colorDirNoisyBlur = mix(dirBlurredA, dirBlurredB, noisyProg2);
  colorNoisyBlur = colorDirNoisyBlur;
  // noise
  vec4 colorDW = domainWarped(mapA, mapB, warpedUV, iTime, prog);
  vec4 colorNoisyDW = noisyDomainWarped(mapA, mapB, warpedUV, iTime, noisyProg1);
  vec4 colorFBM = FBM(mapA, mapB, warpedUV, gnws);
  vec4 colorNoisyFBM = noisyFBM(mapA, mapB, warpedUV, iTime, 0.5, 0.5, gn3, 0.5);

  if (transitionMode == 0) {
    // transition mode 0: Blur
    gl_FragColor = colorBlur;
  } else if (transitionMode == 1) {
    // transition mode 1: Blur with direction
    gl_FragColor = colorDirBlur;
  } else if (transitionMode == 2) {
    // transition mode 2: Blur with noise
    gl_FragColor = colorNoisyBlur;
  } else if (transitionMode == 3) {
    // transition mode 3: Blur with noise and direction
    gl_FragColor = colorDirNoisyBlur;
  } else if (transitionMode == 4) {
    // transition mode 4: Wipe
    gl_FragColor = colorWipe;
  } else if (transitionMode == 5) {
    // transition mode 5: Wipe with noise
    gl_FragColor = colorNoisyWipe;
  } else if (transitionMode == 6) {
    // transition mode 6: FBM
    gl_FragColor = colorFBM;
  } else if (transitionMode == 7) {
    // transition mode 7: FBM with noise
    gl_FragColor = colorNoisyFBM;
  } else if (transitionMode == 8) {
    // transition mode 8: Domain warped 
    gl_FragColor = colorDW;
  } else if (transitionMode == 9) {
    // transition mode 9: Domain warped with noise
    gl_FragColor = colorNoisyDW;
  }
}
`;

export const TransitionShader = {
  vertexShader,
  fragmentShader,
  uniforms: {
    mapA: null,
    mapB: null,
    iTime: 0,
    transitionMode: 0,
    progress: 0,
    direction: [1.0, 0.0],
    // transitionMode: { value: 0 },
    // iTime: { value: 0 },
    // progress: { value: 0 },
    // iResolution: { value: [1, 1] },
    // mouse: { value: [0, 0] },
    // colorA_hsl: { value: [0.5, 0.5, 0.5] },
    // colorB_hsl: { value: [0.5, 0.5, 0.5] },
  },
};
